import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IdeSession } from "~/api/opsTypes";
import { useApi } from "~/api/provider";
import type {
  ReasoningBranchGroup,
  ReasoningCriterion,
  ReasoningEffort,
  ReasoningEvidence,
  ReasoningSession,
  ReasoningSourceBarrier,
  ReasoningTrace,
} from "~/api/reasoningTypes";
import { candidateRecords, evidenceKey, pinnedEvidence } from "~/features/reasoning/candidateDraft";
import { contextManifest, latestCandidate } from "~/features/reasoning/reasoningSelectors";
import { useReasoningSupport } from "~/features/reasoning/useReasoningSupport";
import { Button } from "~/foundation/components/Button";
import type { ActionSpec } from "./usePendingAction";

const text = (value: unknown) => (typeof value === "string" ? value : "");
const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);
/** The statuses after which a branch member may be sent again. */
const RETRYABLE = ["failed", "unavailable", "incomplete", "over-budget", "late"];
const CONTRIBUTION_KINDS = ["analysis", "correction", "alternative", "evidence"] as const;
/** The engine reserves at least this much for an IDE branch (prompt + reserve + headroom). */
const MIN_BRANCH_RESERVATION = 16000;

type Source = { nodeId: string; path?: string; revision?: string };
type MemberDraft = { rowKey: string; purpose: string; targetSessionKey: string };
type CriterionDraft = ReasoningCriterion & { rowKey: string };
const withRowKey = (criterion: ReasoningCriterion): CriterionDraft => ({
  ...criterion,
  rowKey: crypto.randomUUID(),
});
const withoutRowKey = ({ rowKey: _, ...criterion }: CriterionDraft): ReasoningCriterion =>
  criterion;

/**
 * The operations a reasoning session supports beyond the everyday chain — the ones the legacy
 * Run Center panel used to be the only door to: recheck the pinned context, replan the criteria,
 * confirm a changed source, assess a source against a candidate, contribute, compare retained
 * revisions, run and join independent IDE branches, and read every operation's receipts.
 * Collapsed by default; a reader who never opens it never pays for its reads.
 */
export function ReasoningAdvancedSection({
  session,
  trace,
  sourceBarriers,
  busy,
  act,
  thinker,
  effort,
  branchGroupId,
  onBranchGroupChange,
}: {
  session: ReasoningSession;
  trace: ReasoningTrace | null;
  sourceBarriers: ReasoningSourceBarrier[];
  busy: boolean;
  act: (action: ActionSpec) => Promise<unknown>;
  thinker: { id: string; model: string } | undefined;
  effort: ReasoningEffort;
  /** A joined branch group whose result the next "Run agent" reviews. */
  branchGroupId: string;
  onBranchGroupChange: (groupId: string) => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const { limits, branchAgents } = useReasoningSupport();
  const [open, setOpen] = useState(false);
  const manifest = contextManifest(session);
  const contextEvidence = pinnedEvidence(session);
  const candidates = candidateRecords(session);
  const sources = list<Source>(manifest?.sources);
  const corrections = session.records.filter((row) => row.kind === "correction");
  const proposal = session.records.filter((row) => row.kind === "replan-proposal").at(-1);
  const impact = (proposal?.payload.impact ?? {}) as {
    affectedRecordIds?: string[];
    currentAcceptanceRecordId?: string | null;
    baseSessionRevision?: number;
  };
  const command = { sessionId: session.id, expectedSessionRevision: session.revision };

  // ── replan ───────────────────────────────────────────────────────────────
  const [criteriaDraft, setCriteriaDraft] = useState<CriterionDraft[]>(() =>
    session.criteria.map(withRowKey),
  );
  const [replanRationale, setReplanRationale] = useState("");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the draft follows the session's criteria, not its other fields.
  useEffect(() => {
    setCriteriaDraft(session.criteria.map(withRowKey));
  }, [session.id, session.criteriaDigest]);
  const criteriaValid =
    criteriaDraft.length > 0 &&
    criteriaDraft.some((item) => item.hard) &&
    criteriaDraft.every((item) => item.id.trim() && item.description.trim()) &&
    new Set(criteriaDraft.map((item) => item.id)).size === criteriaDraft.length;
  const editCriterion = (index: number, patch: Partial<ReasoningCriterion>) =>
    setCriteriaDraft((rows) => rows.map((row, n) => (n === index ? { ...row, ...patch } : row)));

  // ── assess / contribute / compare ────────────────────────────────────────
  const [selectedId, setSelectedId] = useState("");
  const selected =
    candidates.find((row) => row.id === selectedId) ?? latestCandidate(session) ?? undefined;
  const evidence = selected ? list<ReasoningEvidence>(selected.payload.evidence) : contextEvidence;
  const [assessmentKey, setAssessmentKey] = useState("");
  const assessed = evidence.find((item) => evidenceKey(item) === assessmentKey) ?? evidence[0];
  const [relation, setRelation] = useState<"supports" | "contradicts">("supports");
  const [assessmentRationale, setAssessmentRationale] = useState("");
  const assessments = session.records.filter(
    (row) => row.kind === "evidence-assessment" && row.payload.candidateRecordId === selected?.id,
  );
  const [contributionKind, setContributionKind] = useState<string>("analysis");
  const [contribution, setContribution] = useState("");
  const [contributionRationale, setContributionRationale] = useState("");
  const [compareId, setCompareId] = useState("");
  const comparison =
    candidates.find(
      (row) =>
        row.id === compareId && row.logicalId === selected?.logicalId && row.id !== selected?.id,
    ) ??
    candidates.find(
      (row) =>
        row.logicalId === selected?.logicalId && row.revision === (selected?.revision ?? 0) - 1,
    );

  // ── branches ─────────────────────────────────────────────────────────────
  const [groups, setGroups] = useState<ReasoningBranchGroup[]>([]);
  const [windows, setWindows] = useState<IdeSession[]>([]);
  const [readError, setReadError] = useState("");
  const [members, setMembers] = useState<MemberDraft[]>(() => [
    { rowKey: crypto.randomUUID(), purpose: t("Independent evidence check"), targetSessionKey: "" },
    {
      rowKey: crypto.randomUUID(),
      purpose: t("Alternative answer and objections"),
      targetSessionKey: "",
    },
  ]);
  const [agent, setAgent] = useState("");
  const chosenAgent = agent || branchAgents[0] || "";
  const reloadGroups = async () => {
    try {
      setGroups(await api.reasoning.branchGroups(session.id));
      setReadError("");
    } catch (cause) {
      setReadError(cause instanceof Error ? cause.message : String(cause));
    }
  };
  useEffect(() => {
    if (!open) return;
    let live = true;
    void Promise.allSettled([
      api.reasoning.branchGroups(session.id),
      api.automation.ideSessions.list(),
    ]).then(([branches, ide]) => {
      if (!live) return;
      if (branches.status === "fulfilled") setGroups(branches.value);
      else setReadError(String(branches.reason));
      if (ide.status === "fulfilled") setWindows(ide.value.filter((row) => row.isOnline));
    });
    return () => {
      live = false;
    };
  }, [open, session.id, api]);
  const branchCommand = async (action: ActionSpec) => {
    await act(action);
    await reloadGroups();
  };
  const reservation = Math.max(
    MIN_BRANCH_RESERVATION,
    Number(manifest?.estimatedInputTokens ?? 0) + Number(manifest?.outputReserve ?? 4096) + 2048,
  );
  const membersValid =
    members.length >= 1 &&
    members.length <= limits.maxIndependentBranches &&
    members.every((m) => m.purpose.trim() && m.targetSessionKey) &&
    new Set(members.map((m) => m.targetSessionKey)).size === members.length;

  const operations = trace?.operations ?? [];
  const attention = session.records.filter((row) => row.kind === "attention");
  const contributions = session.records.filter((row) => row.kind === "contribution");

  return (
    <details
      className="rounded border border-divider p-3"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="cursor-pointer font-semibold">{t("Advanced")}</summary>
      <div className="mt-3 flex flex-col gap-5">
        <p className="text-small text-text-secondary">
          {t(
            "Operations on the retained session: its pinned context and sources, the acceptance criteria, source assessments, contributions, revision comparison, independent IDE branches and every model operation's receipts.",
          )}
        </p>
        {readError && (
          <p role="alert" className="text-danger">
            {readError}
          </p>
        )}

        <section className="flex flex-col gap-2" aria-label={t("Pinned context")}>
          <h5 className="font-semibold">{t("Pinned context")}</h5>
          <p className="text-small text-text-secondary">
            {t("Scope {{scope}} · digest {{digest}}", {
              scope: text(manifest?.scopeSlug) || "—",
              digest: text(manifest?.digest).slice(0, 12) || "—",
            })}
          </p>
          {manifest && (
            <div>
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  void act({
                    kind: "refresh-context",
                    input: {
                      ...command,
                      scopeSlug: text(manifest.scopeSlug),
                      query: text(manifest.query),
                      model: text(manifest.model),
                      modelContextWindow: Number(manifest.modelContextWindow ?? 0),
                      outputReserve: Number(manifest.outputReserve ?? 0),
                      promptReserve: Number(manifest.promptReserve ?? 1024),
                      optionalHits: 0,
                      requiredNodeIds: sources.map((source) => source.nodeId),
                      requiredAcceptanceIds: list<{ acceptanceRecordId: string }>(
                        manifest.memories,
                      ).map((memory) => memory.acceptanceRecordId),
                    },
                  })
                }
              >
                {t("Recheck and refresh context")}
              </Button>
            </div>
          )}
          <p className="text-small text-text-secondary">
            {t(
              "After a Canopy source changes, confirm its new version here to invalidate dependent conclusions.",
            )}
          </p>
          <ul className="flex flex-col gap-1">
            {sources.map((source) => {
              const barrier = sourceBarriers.find((row) => row.sourceId === source.nodeId);
              const pinned = contextEvidence.find(
                (row) => row.sourceKind === "canopy" && row.sourceId === source.nodeId,
              );
              return (
                <li key={source.nodeId} className="rounded border border-divider p-2">
                  <span className="break-all">
                    {source.path ?? source.nodeId} ·{" "}
                    {t("revision {{revision}}", { revision: source.revision ?? "?" })}
                  </span>
                  {barrier?.tombstone && (
                    <strong>
                      {" "}
                      · {t("source changed; review the new version before confirming")}
                    </strong>
                  )}
                  <details>
                    <summary>{t("Exact source reference")}</summary>
                    <code className="break-all">{source.nodeId}</code>
                  </details>
                  {pinned && (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        void act({
                          kind: "correct-source",
                          input: {
                            ...command,
                            sourceNodeId: source.nodeId,
                            expectedSourceEpoch: barrier?.epoch ?? pinned.observedEpoch ?? 0,
                            tombstone: false,
                            rationale: t(
                              "Rechecked the current Canopy source from the run workspace",
                            ),
                          },
                        })
                      }
                    >
                      {t("Confirm changed source")}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {list<string>(manifest?.degradation).length > 0 && (
            <p>
              {t("Context degradation: {{items}}", {
                items: list<string>(manifest?.degradation).join(", "),
              })}
            </p>
          )}
          {corrections.length > 0 && (
            <details>
              <summary>{t("Correction impact ({{n}})", { n: corrections.length })}</summary>
              {corrections.map((row) => (
                <div key={row.id} className="mt-2">
                  <p>
                    {text(row.payload.sourceKind)}:{text(row.payload.sourceId)} ·{" "}
                    {t("epoch {{epoch}}", { epoch: String(row.payload.sourceEpoch) })} ·{" "}
                    {t("{{n}} affected records", {
                      n: Number(row.payload.affectedRecordCount ?? 0),
                    })}
                    {row.payload.impactTruncated ? ` (${t("list truncated")})` : ""}
                  </p>
                  <p>{text(row.payload.rationale)}</p>
                  <ul className="list-inside list-disc">
                    {list<string>(row.payload.affectedRecordIds).map((id) => {
                      const affected = session.records.find((record) => record.id === id);
                      return (
                        <li key={id} className="break-all">
                          {affected?.kind ?? t("record")} {id}
                          {affected && !affected.eligible
                            ? ` · ${affected.ineligibilityReason ?? t("needs review")}`
                            : ""}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </details>
          )}
        </section>

        <section className="flex flex-col gap-2" aria-label={t("Replan acceptance criteria")}>
          <h5 className="font-semibold">{t("Replan acceptance criteria")}</h5>
          <p className="text-small text-text-secondary">
            {t(
              "Review the impact before applying a criteria change. Active branch assignments and model operations must settle first.",
            )}
          </p>
          {criteriaDraft.map((item, index) => (
            <div key={item.rowKey} className="flex flex-wrap items-center gap-2">
              <input
                aria-label={t("Criterion ID")}
                value={item.id}
                onChange={(e) => editCriterion(index, { id: e.target.value })}
                className="w-32 rounded border border-divider bg-bg p-1"
              />
              <input
                aria-label={t("Criterion text")}
                value={item.description}
                onChange={(e) => editCriterion(index, { description: e.target.value })}
                className="min-w-48 flex-1 rounded border border-divider bg-bg p-1"
              />
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={item.hard}
                  onChange={(e) => editCriterion(index, { hard: e.target.checked })}
                />
                {t("Hard")}
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={item.requiresEvidence}
                  onChange={(e) => editCriterion(index, { requiresEvidence: e.target.checked })}
                />
                {t("Requires evidence")}
              </label>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setCriteriaDraft((rows) => rows.filter((_, n) => n !== index))}
              >
                {t("Remove")}
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() =>
                setCriteriaDraft((rows) => [
                  ...rows,
                  withRowKey({
                    id: `criterion-${crypto.randomUUID().slice(0, 8)}`,
                    description: "",
                    hard: true,
                    requiresEvidence: true,
                  }),
                ])
              }
            >
              {t("Add criterion")}
            </Button>
          </div>
          <label className="flex flex-col gap-1">
            {t("Why change the criteria?")}
            <textarea
              aria-label={t("Replan rationale")}
              value={replanRationale}
              onChange={(e) => setReplanRationale(e.target.value)}
              className="rounded border border-divider bg-bg p-2"
            />
          </label>
          <div>
            <Button
              size="sm"
              disabled={busy || !replanRationale.trim() || !criteriaValid}
              onClick={() =>
                void act({
                  kind: "replan-propose",
                  input: {
                    ...command,
                    criteria: criteriaDraft.map(withoutRowKey),
                    rationale: replanRationale.trim(),
                  },
                })
              }
            >
              {t("Preview replan")}
            </Button>
          </div>
          {proposal && (
            <p>
              {t("Preview {{id}}: affects {{n}} evaluations or acceptances.", {
                id: proposal.id.slice(0, 8),
                n: (impact.affectedRecordIds ?? []).length,
              })}
              {impact.currentAcceptanceRecordId
                ? ` ${t("Current acceptance will need new review.")}`
                : ""}{" "}
              <Button
                size="sm"
                disabled={
                  busy ||
                  !proposal.eligible ||
                  Number(impact.baseSessionRevision) + 1 !== session.revision
                }
                onClick={() =>
                  void act({
                    kind: "replan-apply",
                    input: {
                      ...command,
                      proposalRecordId: proposal.id,
                      impactDigest: text(proposal.payload.impactDigest),
                    },
                  })
                }
              >
                {t("Apply reviewed replan")}
              </Button>
            </p>
          )}
        </section>

        {candidates.length > 0 && (
          <section className="flex flex-col gap-2" aria-label={t("Assess source relationships")}>
            <h5 className="font-semibold">{t("Assess source relationships")}</h5>
            <p className="text-small text-text-secondary">
              {t(
                "Record why an exact pinned source supports or contradicts this candidate revision.",
              )}
            </p>
            <label>
              {t("Candidate")}{" "}
              <select
                aria-label={t("Assessed candidate")}
                value={selected?.id ?? ""}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                {candidates.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.logicalId} · r{row.revision}
                    {!row.eligible ? ` · ${t("needs review")}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("Evidence source")}{" "}
              <select
                aria-label={t("Evidence source")}
                value={assessed ? evidenceKey(assessed) : ""}
                onChange={(e) => setAssessmentKey(e.target.value)}
              >
                {evidence.map((item) => (
                  <option key={evidenceKey(item)} value={evidenceKey(item)}>
                    {item.sourceKind}:{item.sourceId} · {item.revision}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t("Relationship")}{" "}
              <select
                aria-label={t("Relationship")}
                value={relation}
                onChange={(e) => setRelation(e.target.value as typeof relation)}
              >
                <option value="supports">{t("Supports")}</option>
                <option value="contradicts">{t("Contradicts")}</option>
              </select>
            </label>
            <textarea
              aria-label={t("Assessment rationale")}
              value={assessmentRationale}
              onChange={(e) => setAssessmentRationale(e.target.value)}
              className="rounded border border-divider bg-bg p-2"
            />
            <div>
              <Button
                size="sm"
                disabled={busy || !selected || !assessed || !assessmentRationale.trim()}
                onClick={() => {
                  if (!selected || !assessed) return;
                  void act({
                    kind: "assess-evidence",
                    input: {
                      ...command,
                      candidateRecordId: selected.id,
                      evidence: assessed,
                      relation,
                      rationale: assessmentRationale.trim(),
                    },
                  });
                }}
              >
                {t("Record assessment")}
              </Button>
            </div>
            {assessments.map((row) => (
              <p key={row.id}>
                {text(row.payload.relation)}: {text(row.payload.rationale)}
                {row.eligible ? "" : ` · ${row.ineligibilityReason ?? t("stale")}`}
              </p>
            ))}
          </section>
        )}

        <section className="flex flex-col gap-2" aria-label={t("Contribute")}>
          <h5 className="font-semibold">{t("Contribute")}</h5>
          <p className="text-small text-text-secondary">
            {t("Contributions stay pending until a candidate is revised and evaluated.")}
          </p>
          <label>
            {t("Contribution kind")}{" "}
            <select
              aria-label={t("Contribution kind")}
              value={contributionKind}
              onChange={(e) => setContributionKind(e.target.value)}
            >
              {CONTRIBUTION_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
            </select>
          </label>
          <textarea
            aria-label={t("Contribution")}
            rows={3}
            value={contribution}
            onChange={(e) => setContribution(e.target.value)}
            className="rounded border border-divider bg-bg p-2"
          />
          <input
            aria-label={t("Contribution rationale")}
            placeholder={t("Why this contribution? (optional)")}
            value={contributionRationale}
            onChange={(e) => setContributionRationale(e.target.value)}
            className="rounded border border-divider bg-bg p-2"
          />
          <div>
            <Button
              size="sm"
              disabled={busy || !contribution.trim()}
              onClick={() =>
                void act({
                  kind: "contribute",
                  input: {
                    ...command,
                    kind: contributionKind,
                    content: contribution.trim(),
                    rationale:
                      contributionRationale.trim() ||
                      t("Submitted from the run reasoning workspace"),
                    baseCandidateRecordId: selected?.id,
                    evidence,
                  },
                })
              }
            >
              {t("Submit contribution")}
            </Button>
          </div>
        </section>

        {selected && (
          <section className="flex flex-col gap-2" aria-label={t("Compare retained revisions")}>
            <h5 className="font-semibold">{t("Compare retained revisions")}</h5>
            <label>
              {t("Compare with")}{" "}
              <select
                aria-label={t("Compare revision")}
                value={comparison?.id ?? ""}
                onChange={(e) => setCompareId(e.target.value)}
              >
                <option value="">{t("Choose a revision")}</option>
                {candidates
                  .filter((row) => row.logicalId === selected.logicalId && row.id !== selected.id)
                  .map((row) => (
                    <option key={row.id} value={row.id}>
                      {t("Revision {{n}}", { n: row.revision })}
                    </option>
                  ))}
              </select>
            </label>
            {comparison ? (
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <strong>{t("Revision {{n}}", { n: comparison.revision })}</strong>
                  <pre className="max-h-64 overflow-auto whitespace-pre-wrap">
                    {text(comparison.payload.content)}
                  </pre>
                </div>
                <div>
                  <strong>{t("Revision {{n}}", { n: selected.revision })}</strong>
                  <pre className="max-h-64 overflow-auto whitespace-pre-wrap">
                    {text(selected.payload.content)}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-small text-text-secondary">
                {t("This candidate has no other retained revision to compare with.")}
              </p>
            )}
          </section>
        )}

        <section className="flex flex-col gap-2" aria-label={t("Independent branches")}>
          <h5 className="font-semibold">{t("Independent branches")}</h5>
          <p className="text-small text-text-secondary">
            {t(
              "Assign separate live IDE windows; each works from the exact pinned context with tools off. All required results must arrive before joining.",
            )}
          </p>
          {branchAgents.length === 0 ? (
            <p className="text-small text-text-secondary">
              {t("The server has not said which IDE agents may run a branch.")}
            </p>
          ) : (
            <label>
              {t("Branch agent")}{" "}
              <select
                aria-label={t("Branch agent")}
                value={chosenAgent}
                onChange={(e) => setAgent(e.target.value)}
              >
                {branchAgents.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {members.map((member, index) => (
            <div key={member.rowKey} className="flex flex-wrap items-center gap-2">
              <input
                aria-label={t("Purpose {{n}}", { n: index + 1 })}
                value={member.purpose}
                onChange={(e) =>
                  setMembers((rows) =>
                    rows.map((row, n) => (n === index ? { ...row, purpose: e.target.value } : row)),
                  )
                }
                className="min-w-48 flex-1 rounded border border-divider bg-bg p-1"
              />
              <select
                aria-label={t("Window {{n}}", { n: index + 1 })}
                value={member.targetSessionKey}
                onChange={(e) =>
                  setMembers((rows) =>
                    rows.map((row, n) =>
                      n === index ? { ...row, targetSessionKey: e.target.value } : row,
                    ),
                  )
                }
              >
                <option value="">{t("Choose a live window")}</option>
                {windows.map((row) => (
                  <option key={row.sessionKey} value={row.sessionKey}>
                    {row.workspaceName || row.machineName || row.sessionKey}
                  </option>
                ))}
              </select>
              {members.length > 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setMembers((rows) => rows.filter((_, n) => n !== index))}
                >
                  {t("Remove")}
                </Button>
              )}
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={members.length >= limits.maxIndependentBranches}
              onClick={() =>
                setMembers((rows) => [
                  ...rows,
                  { rowKey: crypto.randomUUID(), purpose: "", targetSessionKey: "" },
                ])
              }
            >
              {t("Add branch")}
            </Button>
            <Button
              size="sm"
              disabled={busy || !membersValid || !manifest || !thinker?.model || !chosenAgent}
              onClick={() => {
                if (!thinker?.model) return;
                void branchCommand({
                  kind: "branch-activate",
                  input: {
                    ...command,
                    joinMode: "all",
                    quorum: 0,
                    members: members.map((member, index) => ({
                      memberId: `independent-${index + 1}`,
                      purpose: member.purpose.trim(),
                      targetSessionKey: member.targetSessionKey,
                      agent: chosenAgent,
                      model: thinker.model,
                      effort,
                      mandatory: true,
                      tokenReservation: reservation,
                    })),
                  },
                });
              }}
            >
              {t("Activate branches")}
            </Button>
          </div>
          <p className="text-small text-text-secondary">
            {t("Up to {{max}} branches; each reserves {{tokens}} tokens.", {
              max: limits.maxIndependentBranches,
              tokens: reservation.toLocaleString(),
            })}
          </p>
          {windows.length === 0 && (
            <p className="text-small text-text-secondary">
              {t("No live IDE window is connected. Open the Docit extension in VS Code first.")}
            </p>
          )}
          {branchGroupId && (
            <p>
              {t("The next run reviews joined group {{id}}.", { id: branchGroupId.slice(0, 8) })}{" "}
              <Button size="sm" variant="ghost" onClick={() => onBranchGroupChange("")}>
                {t("Clear")}
              </Button>
            </p>
          )}
          {groups.map((group) => (
            <div key={group.id} className="rounded border border-divider p-2">
              <p>
                {t("Group {{id}} · {{status}} · {{joinMode}}", {
                  id: group.id.slice(0, 8),
                  status: group.status,
                  joinMode: group.joinMode,
                })}
              </p>
              <ul className="list-inside list-disc">
                {group.members.map((member) => {
                  const attempt = member.attempts.at(-1);
                  return (
                    <li key={member.memberId}>
                      {member.memberId}: {member.status}
                      {RETRYABLE.includes(member.status) &&
                        group.status === "activated" &&
                        attempt && (
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                              void branchCommand({
                                kind: "branch-retry",
                                input: {
                                  ...command,
                                  groupId: group.id,
                                  memberId: member.memberId,
                                  expectedAttemptId: attempt.id,
                                  targetSessionKey: attempt.targetSessionKey ?? "",
                                },
                              })
                            }
                          >
                            {t("Retry")}
                          </Button>
                        )}
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-wrap gap-2">
                {group.status === "activated" && (
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void branchCommand({
                        kind: "branch-join",
                        input: { ...command, groupId: group.id },
                      })
                    }
                  >
                    {t("Join completed branches")}
                  </Button>
                )}
                {group.status === "activated" && (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() =>
                      void branchCommand({
                        kind: "branch-cancel",
                        input: {
                          ...command,
                          groupId: group.id,
                          rationale: t("Cancelled from the run reasoning workspace"),
                        },
                      })
                    }
                  >
                    {t("Cancel branches")}
                  </Button>
                )}
                {group.status === "joined" && (
                  <Button size="sm" onClick={() => onBranchGroupChange(group.id)}>
                    {t("Use joined result for final review")}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void reloadGroups()}
                >
                  {t("Refresh groups")}
                </Button>
              </div>
            </div>
          ))}
        </section>

        <section className="flex flex-col gap-2" aria-label={t("Model operations")}>
          <h5 className="font-semibold">{t("Model operations")}</h5>
          {operations.length === 0 && (
            <p className="text-small text-text-secondary">{t("No model operation has run yet.")}</p>
          )}
          {operations.map((operation) => (
            <details key={operation.id} className="rounded border border-divider p-2">
              <summary>
                {operation.status} · {operation.requestedModel} ·{" "}
                {t("{{n}} tokens", { n: operation.inputTokens + operation.outputTokens })}
              </summary>
              <p className="break-all text-small">
                {t("Operation {{id}} · thinker {{thinker}} · effort {{effort}}", {
                  id: operation.id,
                  thinker: operation.thinkerId,
                  effort: operation.requestedEffort,
                })}
              </p>
              <p className="break-all text-small">
                {t("Context {{context}} · criteria {{criteria}} · reserved {{tokens}} tokens", {
                  context: operation.contextRecordId ?? "—",
                  criteria: (operation.criteriaDigest ?? "—").slice(0, 12),
                  tokens: operation.reservedTokens,
                })}
              </p>
              {operation.error && <p className="text-danger">{operation.error}</p>}
              {operation.receiptGap && (
                <p role="status">
                  {t("Trace gap: a completed operation is missing a provider receipt.")}
                </p>
              )}
              {operation.journalGap && (
                <p role="status">
                  {t(
                    "Trace gap: an operation stage is missing its dispatch, receipt, application, or settlement event.",
                  )}
                </p>
              )}
              {!operation.generation && (
                <p>{t("Generation receipt absent or invocation uncertain.")}</p>
              )}
              {!operation.evaluation && (
                <p>{t("Evaluation receipt absent or review not reached.")}</p>
              )}
              {operation.generation && (
                <p className="break-all text-small">
                  {t("Generation request {{id}} · digest {{digest}}", {
                    id: operation.generation.requestId ?? t("unknown"),
                    digest: operation.generation.fullTextDigest ?? t("unavailable"),
                  })}
                  {operation.generation.truncated ? ` · ${t("retained text truncated")}` : ""}
                  {operation.generation.unreadable ? ` · ${t("unreadable receipt")}` : ""}
                </p>
              )}
              {operation.evaluation && (
                <p className="break-all text-small">
                  {t("Evaluation request {{id}} · digest {{digest}}", {
                    id: operation.evaluation.requestId ?? t("unknown"),
                    digest: operation.evaluation.fullTextDigest ?? t("unavailable"),
                  })}
                  {operation.evaluation.truncated ? ` · ${t("retained text truncated")}` : ""}
                  {operation.evaluation.unreadable ? ` · ${t("unreadable receipt")}` : ""}
                </p>
              )}
              {operation.candidateRecordId && (
                <p className="break-all text-small">
                  {t("Candidate {{id}}", { id: operation.candidateRecordId })}
                </p>
              )}
              {operation.evaluationRecordId && (
                <p className="break-all text-small">
                  {t("Evaluation {{id}}", { id: operation.evaluationRecordId })}
                </p>
              )}
            </details>
          ))}
        </section>

        {(attention.length > 0 || contributions.length > 0) && (
          <section className="flex flex-col gap-2" aria-label={t("Retained history")}>
            <h5 className="font-semibold">{t("Retained history")}</h5>
            {attention.map((row) => (
              <details key={row.id}>
                <summary>
                  {t("Causal attention for {{key}} · {{verdict}}", {
                    key: text(row.payload.workItemKey),
                    verdict: text(row.payload.verdict),
                  })}
                </summary>
                <p>
                  {t("Read on {{asOf}}.", { asOf: text(row.payload.asOf) })}{" "}
                  {text(row.payload.reason)}
                </p>
                <p>
                  {t("Declared prerequisites: {{items}}", {
                    items:
                      list<string>(row.payload.declaredPrerequisites).join(", ") ||
                      t("none recorded"),
                  })}
                </p>
                <p>
                  {t("Measured associations: {{items}}", {
                    items:
                      list<string>(row.payload.measuredAssociations).join(", ") ||
                      t("none supported"),
                  })}
                </p>
                <p>
                  {t("Inspection order: {{items}}", {
                    items:
                      list<{ workItemKey: string; basis: string }>(row.payload.inspectionOrder)
                        .map((target) => `${target.workItemKey} (${target.basis})`)
                        .join(" → ") || t("unavailable"),
                  })}
                </p>
                <p className="text-small text-text-secondary">
                  {t(
                    "Measured association does not establish causation or change action authority.",
                  )}
                </p>
              </details>
            ))}
            {contributions.map((row) => (
              <p key={row.id}>
                {t("Pending {{kind}}: {{content}}", {
                  kind: text(row.payload.kind),
                  content: text(row.payload.content),
                })}
              </p>
            ))}
          </section>
        )}
      </div>
    </details>
  );
}
